<template>
  <HorizontalDragScroll v-if="data" :class="classes" :style="styles" :snap="true" :scrollbar="'none'">
    <div v-for="(column, i) in data.columns" :key="column.sys.id" class="grid-row-item" :style="gridItemStyles[i]">
      <suspense>
        <ContentfulDynamicRenderer v-bind="column" />
      </suspense>
    </div>
  </HorizontalDragScroll>
</template>

<script>
import ContentfulDynamicRenderer from '../Fields/ContentfulDynamicRenderer.vue';
import HorizontalDragScroll from '@/components/commons/HorizontalDragScroll.vue';
import { useContentful, contentfulProps } from '@/compositions/useContentful.js';
import useUniqueId from '@/compositions/useUniqueId.js';
import useResize from '@/compositions/useResize.js';
import { computed } from 'vue';

export default {
  components: {
    ContentfulDynamicRenderer, //
    HorizontalDragScroll,
  },
  props: contentfulProps,
  async setup(p, c) {
    const { uid } = useUniqueId();
    const { data, init } = useContentful('gridRow', p, c);

    const classes = computed(() => {
      const c = ['grid-row', `grid-row-${uid}`];
      if (!data.value) {
        return c;
      }
      if (data.value.fullWidth) c.push('bleedout');
      if (!currentGridColumns.value || currentGridColumns.value.some((x) => Number.isNaN(breakItem(x, NaN).colspan))) c.push('stacked');
      if (data.value.verticalAlign) c.push(`valign-${data.value.verticalAlign}`);
      if (data.value.removeGap) c.push('nogap');
      if (isPercentageGrid.value) c.push('gridrow-carousel');
      return c;
    });
    const styles = computed(() => {
      if (!data.value) {
        return '';
      }
      let styles = [];
      if (data.value.backgroundColor) {
        styles.push(`background-color:${data.value.backgroundColor}`);
      }
      if (isPercentageGrid.value) {
        // Use percentage widths for grid row content and behave like a carousel.
        styles.push(`grid-template-columns: ${currentGridColumns.value.join(' ')}`);
      } else if (currentGridColumns.value) {
        const numColumns = currentGridColumns.value.reduce((acc, cur) => breakItem(cur, 0).colspan + acc, 0);
        styles.push(`grid-template-columns: ${'1fr '.repeat(numColumns)}`);
      }
      return styles.join(';');
    });

    const isPercentageGrid = computed(() => {
      return (
        currentGridColumns.value && currentGridColumns.value.filter((col) => col && col.endsWith('%')).length === currentGridColumns.value.length
      );
    });

    const breakItem = (item, _default) => {
      const part = item.split('/');
      const colspan = Number(part[0]);
      const order = part[1] || -1;
      return { colspan: Number.isNaN(colspan) ? _default : colspan, order };
    };

    const { width } = useResize();

    const currentGridColumns = computed(() => {
      if (!data.value) {
        return null;
      }
      if (width.value < 768) {
        return data.value.ratio320px;
      }
      if (width.value < 1200) {
        return data.value.ratio768px;
      }
      if (width.value < 1400) {
        return data.value.ratio1200px;
      }
      if (width.value < 1800) {
        return data.value.ratio1400px;
      }
      return data.value.ratio1800px;
    });

    const gridItemStyles = computed(() => {
      if (!data.value || !currentGridColumns.value) {
        return [];
      }
      const result = [];
      currentGridColumns.value.reduce((acc, cur) => {
        const item = breakItem(cur, NaN);

        const start = acc;
        const end = acc + item.colspan;
        if (Number.isNaN(item.colspan)) {
          if (item.order !== -1) {
            result.push({ 'grid-row': `${item.order}/${item.order}` });
          }
        } else if (item.colspan === 0) {
          result.push({ display: 'none' });
        } else {
          result.push({ 'grid-column': `${start}/${end}` });
        }
        return end;
      }, 1);
      return result;
    });

    await init();

    return {
      data,
      classes,
      styles,
      uid,
      gridItemStyles,
      currentGridColumns,
    };
  },
};
</script>

<style lang="postcss">
@import '@/pixel/style/variables';
@import '@/pixel/style/mixins';

@define-mixin applyGap $horizontalSpace, $verticalSpace, $gap, $fs {
  gap: $gap;
  padding-top: $verticalSpace;
  padding-bottom: $verticalSpace;

  .grid-row-item > .contentful-tile .description {
    font-size: $fs;
  }
}

@define-mixin applyGapInside $horizontalSpace, $verticalSpace, $gap, $fs {
  .grid-row-item {
    & > .contentful-richtext {
      margin: $verticalSpace $gap;
    }
    & > .contentful-tile .description {
      font-size: $fs;
    }
  }
}

/* purgecss start ignore */
.grid-row {
  display: grid;
  .grid-row-item {
    & > .spacing-hero,
    & > .spacing-large {
      margin: 0;
    }
  }

  &.valign-top {
    .grid-row-item {
      align-self: start;
    }
    &.nogap {
      .grid-row-item {
        display: flex;
        align-items: flex-start;
      }
    }
  }
  &.valign-center {
    .grid-row-item {
      align-self: center;
    }
    &.nogap {
      .grid-row-item {
        display: flex;
        align-items: center;
      }
    }
  }
  &.valign-bottom {
    .grid-row-item {
      align-self: end;
    }
    &.nogap {
      .grid-row-item {
        display: flex;
        align-items: flex-end;
      }
    }
  }

  &.bleedout.stacked {
    padding-left: 0 !important;
    padding-right: 0 !important;

    .grid-row-item > .contentful-tile.has-link.bleedout.to-edge {
      .description {
        margin-left: 20px;
        margin-right: 20px;
      }
    }
    &.nogap {
      .contentful-richtext {
        margin: 0;
      }
    }
  }
  &.bleedout.stacked .grid-row-item > *:not(.bleedout):not(.to-edge) {
    @mixin responsiveContainerPadding;
  }

  &.gridrow-carousel {
    overflow-x: scroll;
    > .grid-row-item {
      &:last-child {
        @mixin responsiveContainerPadding;
        padding-left: 0;
        width: 100%;
      }
    }
  }

  &.nogap {
    padding: 0 !important;
    .grid-row-item {
      height: 100%;
      .contentful-responsive-asset,
      .contentful-responsive-asset * {
        padding: 0 !important;
        height: 100%;
        width: 100%;
        object-fit: cover;
      }
      .contentful-responsive-asset .description{
        display: none;
      }
    }
    @mixin applyGapInside 20px, 40px, 24px, 16px;

    @media (min-width: $mobile-breakpoint) {
      @mixin applyGapInside 24px, 40px, 40px, 16px;
    }
    @media (min-width: $desktop-breakpoint) {
      @mixin applyGapInside 40px, 40px, 40px, 20px;
    }
    @media (min-width: $large-breakpoint) {
      @mixin applyGapInside 40px, 40px, 64px, 20px;
    }
    @media (min-width: $xxl-breakpoint) {
      @mixin applyGapInside 80px, 80px, 80px, 24px;
    }
  }

  &:not(.nogap) {
    @mixin applyGap 20px, 40px, 24px, 16px;

    @media (min-width: $mobile-breakpoint) {
      @mixin applyGap 24px, 40px, 40px, 16px;
    }
    @media (min-width: $desktop-breakpoint) {
      @mixin applyGap 40px, 40px, 40px, 20px;
    }
    @media (min-width: $large-breakpoint) {
      @mixin applyGap 40px, 40px, 64px, 20px;
    }
    @media (min-width: $xxl-breakpoint) {
      @mixin applyGap 80px, 80px, 80px, 24px;
    }
  }

  .grid-row-item {
    & > .contentful-tile.has-link {
      .description {
        margin: 0;
        max-width: unset;
        padding: 0;
        margin-top: 1em;
        margin-bottom: 1em;
        .preview {
          display: flex;
          justify-content: space-between;
          text-align: left;
          .tile-link {
            text-align: right;
            margin-left: 8px;
          }
        }
      }
    }
  }
}
/* purgecss end ignore */
</style>
